import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TreeNode } from '../../core/utils/tree.util';

/**
 * Tree Expand Column Component
 * Renders expand/collapse button with proper indentation for tree nodes
 *
 * Features:
 * - Visual indentation per level
 * - Expand/collapse icon
 * - Smooth animations
 * - Accessibility support
 * - Shows/hides expand icon based on hasChildren
 */
@Component({
  selector: 'app-tree-expand-column',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule],
  template: `
    <div class="tree-expand-column-container" [style.padding-left.px]="indentSize * level">
      <button
        *ngIf="hasChildren"
        mat-icon-button
        class="tree-expand-button"
        (click)="onToggle($event)"
        [attr.aria-label]="expanded ? 'Collapse' : 'Expand'"
        [attr.aria-expanded]="expanded"
        matTooltip="{{ expanded ? 'Colapsar' : 'Expandir' }}"
        matTooltipPosition="right">
        <mat-icon class="tree-expand-icon" [class.expanded]="expanded">
          {{ expanded ? 'expand_more' : 'chevron_right' }}
        </mat-icon>
      </button>

      <!-- Placeholder to maintain alignment when no children -->
      <div *ngIf="!hasChildren" class="tree-no-children-placeholder"></div>
    </div>
  `,
  styles: [`
    .tree-expand-column-container {
      @apply flex items-center;
      min-height: 40px;
    }

    .tree-expand-button {
      @apply w-8 h-8 flex items-center justify-center;
      @apply transition-all duration-200 ease-in-out;

      &:hover {
        @apply bg-gray-100 dark:bg-gray-700;
      }
    }

    .tree-expand-icon {
      @apply text-gray-600 dark:text-gray-400;
      @apply transition-transform duration-200 ease-in-out;
      font-size: 20px;
      width: 20px;
      height: 20px;

      &.expanded {
        @apply transform rotate-0;
      }
    }

    .tree-no-children-placeholder {
      @apply w-8 h-8;
      display: inline-block;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeExpandColumnComponent {
  /**
   * Whether node is expanded
   */
  @Input() expanded: boolean = false;

  /**
   * Whether node has children
   */
  @Input() hasChildren: boolean = false;

  /**
   * Node level/depth (0-based)
   */
  @Input() level: number = 0;

  /**
   * Indentation size per level in pixels
   * @default 20
   */
  @Input() indentSize: number = 20;

  /**
   * Emits when expand/collapse is toggled
   */
  @Output() toggle = new EventEmitter<void>();

  /**
   * Handle toggle click
   * Stop propagation to prevent row click events
   */
  onToggle(event: MouseEvent): void {
    event.stopPropagation();
    this.toggle.emit();
  }
}
